import type { ISorter } from "./local-sorter"

export interface IRemoteSorterConfig {
   queryParamName: string
}

/**
 * This class provides convinient way to parse/stringify sorters which are used to tell backend how result should be sort.
 *
 * How to use it:
 * ```ts
   import { RemoteSorters } from "design-system-ui-components"

   const sorters = new RemoteSorters()
   sorters.add({ property: "name", direction: "ASC" })
   sorters.add({ property: "age", direction: "DESC" })
   const urlWithSorters = "http://domain.com/?" + sorters.stringify()

   "urlWithSorters" is:
   `http://domain.com/?sorters=%5B%7B%22property%22%3A%22name%22%2C%22direction%22%3A%22ASC%22%7D%2C%7B%22
      property%22%3A%22age%22%2C%22direction%22%3A%22DESC%22%7D%5D`

   We can also parse sorters from url (lets assume we have url like above).
   const sorters = new RemoteSorters()
   sorters.parse()

   Result of "sorters.getAll()" is:
   [
      { property: "name", direction: "ASC" },
      { property: "age", direction: "DESC" }
   ]

 * ```
 */
export class RemoteSorters {
   private queryParamName = "sorters"
   private items: ISorter[] = []

   /**
    * Contructor
    * @param config - Config where we can setup e.g. `queryParamName`
    */
   constructor(config?: IRemoteSorterConfig) {
      config?.queryParamName
         ? (this.queryParamName = config.queryParamName)
         : null
   }

   /**
    * Gets the sorter object with given property name.
    *
    * @param property - Property name which is used to find sorter object
    */
   get(property: ISorter["property"]) {
      return this.items.find((sorter) => {
         return sorter.property === property
      })
   }

   /**
    * Returns all sorters.
    */
   getAll(): ISorter[] {
      return this.items
   }

   /**
    * Add sorter.
    */
   add(filter: ISorter) {
      this.items.push(filter)
   }

   /**
    * Remove sorter with given property name
    * @param property - Property name which is used to find sorter
    */
   remove(property: ISorter["property"]) {
      this.items = this.items.filter((sorter) => {
         return sorter.property !== property
      })
   }

   /**
    * Method parse `window.location.search`.
    * All found sorters from uri are added and can be retrived using methods `get` and `getAll`.
    *
    * @param urlSearchParams - String which must be like => filter=encoded(json)
    */
   parse(urlSearchParams?: string) {
      const params = new URLSearchParams(
         urlSearchParams ? urlSearchParams : window.location.search
      )

      if (params.has(this.queryParamName)) {
         try {
            const sorters = decodeURIComponent(
               params.get(this.queryParamName) || ""
            )
            this.items = JSON.parse(sorters)
         } catch (err) {
            console.error(err)
         }
      }
   }

   /**
    * It stringify all sorters as a uriEncoded json.
    */
   stringify() {
      return (
         this.queryParamName +
         "=" +
         encodeURIComponent(JSON.stringify(this.items))
      )
   }
}
